/**
 * AffiliateAgreement — renders the Affiliate Program Agreement from the
 * `legal.affiliateAgreement` i18n tree.
 *
 * Structured data rather than markup, the same way `LegalContent` renders Terms
 * of Use, so the copy stays translatable and no legal text is embedded in a
 * component. Section headings carry their own numbering in the source copy.
 */
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import './affiliatePortal.css';

/** Source order of `legal.affiliateAgreement.sections`; JSON key order is not contractual. */
const SECTION_KEYS = [
  'enrollment',
  'independentContractor',
  'promotion',
  'compliance',
  'linksAndTracking',
  'commissions',
  'intellectualProperty',
  'prohibitedActivities',
  'confidentialInformation',
  'limitationOfLiability',
  'indemnification',
  'noGuarantee',
  'modification',
  'termination',
  'governingLaw',
  'entireAgreement',
  'mission',
  'acceptance',
] as const;

interface AgreementBlock {
  text?: string;
  bullets?: string[];
  strong?: boolean;
}

interface AgreementSection {
  heading?: string;
  blocks?: AgreementBlock[];
}

interface AffiliateAgreementProps {
  /** Constrains the document to a scrolling region, for use inside a dialog. */
  scrollable?: boolean;
}

export function AffiliateAgreement({ scrollable = false }: AffiliateAgreementProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`affiliate-agreement${
        scrollable ? ' affiliate-agreement__scroll' : ''
      }`}
    >
      <p className="affiliate-agreement__date">{t('legal.affiliateAgreement.lastUpdated')}</p>
      <p className="affiliate-agreement__intro">{t('legal.affiliateAgreement.intro')}</p>
      <ol className="affiliate-agreement__sections">
        {SECTION_KEYS.map(key => {
          const section = t(`legal.affiliateAgreement.sections.${key}`, {
            returnObjects: true,
          }) as AgreementSection | string;
          // A missing key resolves to the key itself; skip rather than print it.
          if (typeof section === 'string') return null;

          return (
            <li className="affiliate-agreement__section" key={key}>
              {/* The closing mission statement is deliberately unheaded copy;
                  an empty h3 would be announced as a blank heading. */}
              {section.heading && (
                <h3 className="affiliate-agreement__heading">{section.heading}</h3>
              )}
              {section.blocks?.map((block, index) =>
                block.bullets ? (
                  <ul className="affiliate-agreement__bullets" key={index}>
                    {block.bullets.map(bullet => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                ) : (
                  <p
                    className={`affiliate-agreement__paragraph${
                      block.strong ? ' affiliate-agreement__paragraph--strong' : ''
                    }`}
                    key={index}
                  >
                    {block.text}
                  </p>
                ),
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
